// js/shared/utm.js — the BROWSER half of campaign attribution.
//
// QUEUE ITEM IE. The vocabulary and the three deciding functions are in
// js/shared/campaigns.js, which is pure and is what /admin's eager report
// module imports. This file is what a PUBLIC PAGE loads: it remembers the
// campaign for the visit and bridges the readers onto `window` for the two
// inline form handlers. Nothing here runs on a signed-in surface, which is the
// whole reason it is not in the other file — see that file's header for the
// measurement.
//
// ── WHY IT IS REMEMBERED FOR THE VISIT ─────────────────────────────────────
//
// The card page offers "Website" as well as "Ask for a free call", so the
// real path is often card → home page → contact form, and the parameter is on
// neither of the last two URLs. First-touch attribution is what every analytics
// product does here (GA4's session-scoped source, HubSpot's original-source
// cookie): the FIRST campaign seen in a visit wins, and a later page without a
// parameter does not erase it.
//
// `sessionStorage` rather than a cookie or `localStorage`: it is scoped to the
// tab and expires with it, so it needs no consent banner and cannot follow
// somebody across weeks. Nothing about a PERSON is stored — the value is one of
// the codes in `CAMPAIGNS`, and the code is the same for every visitor who
// scanned the same card.
//
// It runs on LOAD rather than at submit, because the parameter is on the URL of
// the page the visitor ARRIVED on and by the time they press Send they may be
// two pages past it. A blocked or full sessionStorage is not an error worth
// showing anybody: the campaign is lost for that visit and the lead still
// arrives, which is the right side to fail towards.

import { CAMPAIGNS, campaignFrom, campaignLabel, leadSourceLabel } from './campaigns.js';

export { CAMPAIGNS, campaignFrom, campaignLabel, leadSourceLabel };

const STORAGE_KEY = 'kinetiqaf_campaign';

/** Remember this visit's campaign, and return it. Safe to call more than once. */
export function rememberCampaign(win) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  if (!w) return null;
  let stored = null;
  try { stored = w.sessionStorage.getItem(STORAGE_KEY); } catch (_) { stored = null; }
  const code = campaignFrom({ search: (w.location || {}).search, stored });
  if (code && code !== stored) {
    try { w.sessionStorage.setItem(STORAGE_KEY, code); } catch (_) { /* storage blocked — this visit only */ }
  }
  return code;
}

if (typeof window !== 'undefined') {
  // `kafCampaignCode` is what index.html's and intake.html's inline submit
  // handlers put in their payload. A FUNCTION rather than a captured constant,
  // so a form submitted after a client-side navigation reads the campaign that
  // is true then.
  window.kafCampaignCode = () => rememberCampaign(window);
  rememberCampaign(window);
}
